import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import { parseTicketCSV, calculateStatistics, filterTicketsByDate, toMillimes, fromMillimes } from '../utils/csvParser';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import './RevenueDashboardPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const RevenueDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [allTickets, setAllTickets] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [error, setError] = useState('');
  const [fixedCharges, setFixedCharges] = useState([]);
  const [expenses, setExpenses] = useState([]);
  
  // Filtres de dates
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [excludedDates, setExcludedDates] = useState([]);
  const [newExcludedDate, setNewExcludedDate] = useState('');
  const [daysToShow, setDaysToShow] = useState(30); // Nombre de jours à afficher par défaut
  const [platesSortBy, setPlatesSortBy] = useState('count'); // 'count', 'revenue', 'name', 'rank'
  const [platesSortOrder, setPlatesSortOrder] = useState('desc'); // 'asc', 'desc'
  const [selectedDateDetails, setSelectedDateDetails] = useState(null); // Date sélectionnée pour voir les détails
  const [showDetailsModal, setShowDetailsModal] = useState(false); // Afficher la popup de détails
  const [csvDownloadUrl, setCsvDownloadUrl] = useState(null); // URL du CSV pour téléchargement
  const [dataVersion, setDataVersion] = useState(0); // Version pour forcer le rechargement
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { role: userRole } = useAdminRole();

  // Détecter la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Gérer le tri depuis les colonnes
  const handleColumnSort = (column) => {
    if (platesSortBy === column) {
      // Si on clique sur la même colonne, inverser l'ordre
      setPlatesSortOrder(platesSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Sinon, changer la colonne et mettre en ordre décroissant par défaut
      setPlatesSortBy(column);
      setPlatesSortOrder('desc');
    }
  };

  // Vérifier l'état d'authentification
  useEffect(() => {
    // Forcer le rechargement des données à chaque chargement de la page
    // En incrémentant dataVersion, on force la recréation des graphiques
    setDataVersion(prev => prev + 1);
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (!currentUser) {
        navigate('/admin');
      } else {
        // Attendre un peu pour s'assurer que l'utilisateur est bien authentifié
        console.log('Utilisateur authentifié:', currentUser.uid, currentUser.email);
        console.log('🔄 Chargement des données (cache désactivé)...');
        await loadCSVData();
        await loadFixedCharges();
        await loadExpenses();
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Charger et analyser les données CSV depuis Firebase Storage
  const loadCSVData = async () => {
    setDataLoading(true);
    setError('');
    
    // Vérifier que l'utilisateur est authentifié
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('Utilisateur non authentifié');
      setError('Vous devez être connecté pour charger les données.');
      setDataLoading(false);
      return;
    }
    
    console.log('Chargement CSV - Utilisateur:', currentUser.uid, currentUser.email);
    
    try {
      // Chercher le fichier CSV dans Firebase Storage (priorité à merged_revenue.csv)
      console.log('Recherche des fichiers dans revenue_file/...');
      const revenueFilesRef = ref(storage, 'revenue_file/');
      const filesList = await listAll(revenueFilesRef);
      console.log('Fichiers trouvés:', filesList.items.length);
      
      if (filesList.items.length === 0) {
        throw new Error('Aucun fichier CSV trouvé. Veuillez uploader un fichier dans la page "Upload Revenus".');
      }
      
      // Chercher merged_revenue.csv en priorité, sinon prendre le premier fichier
      let fileRef = null;
      const mergedFile = filesList.items.find(item => item.name === 'merged_revenue.csv');
      if (mergedFile) {
        fileRef = mergedFile;
        console.log('Fichier fusionné trouvé:', fileRef.name, fileRef.fullPath);
      } else {
        fileRef = filesList.items[0];
        console.log('Fichier trouvé:', fileRef.name, fileRef.fullPath);
      }
      
      // Obtenir les métadonnées du fichier pour forcer le rechargement
      console.log('Récupération des métadonnées du fichier...');
      const metadata = await getMetadata(fileRef);
      const fileUpdatedTime = metadata.updated || metadata.timeCreated || Date.now();
      console.log('Métadonnées du fichier:', {
        name: metadata.name,
        size: metadata.size,
        updated: metadata.updated,
        timeCreated: metadata.timeCreated,
        contentType: metadata.contentType
      });
      
      // Forcer le rechargement sans cache en utilisant getDownloadURL avec cache-busting
      // Puis télécharger via fetch avec option cache: 'no-store' (sans headers personnalisés pour éviter CORS)
      console.log('Téléchargement du fichier avec cache-busting forcé...');
      
      // Obtenir l'URL de téléchargement avec cache-busting
      const downloadUrl = await getDownloadURL(fileRef);
      // Utiliser un timestamp unique à chaque chargement pour forcer le rechargement
      const urlWithCacheBust = `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}t=${fileUpdatedTime}&_=${Date.now()}`;
      
      // Télécharger via fetch avec cache: 'no-store' (sans headers personnalisés pour éviter les erreurs CORS)
      const response = await fetch(urlWithCacheBust, {
        cache: 'no-store' // Force le rechargement sans utiliser le cache
      });
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log('Blob téléchargé (sans cache):', blob.size, 'octets, type:', blob.type, 'version:', dataVersion, 'fileUpdated:', fileUpdatedTime);
      
      // Convertir le blob en texte
      const csvContent = await blob.text();
      console.log('Contenu CSV décodé, longueur:', csvContent.length);
      
      if (!csvContent || csvContent.trim().length === 0) {
        throw new Error('Le fichier CSV est vide');
      }
      
      // Sauvegarder l'URL de téléchargement avec cache-busting basé sur les métadonnées du fichier
      // Utiliser le timestamp de mise à jour du fichier pour forcer le rechargement
      // L'URL est déjà créée ci-dessus avec cache-busting
      setCsvDownloadUrl(urlWithCacheBust);
      console.log('URL de téléchargement avec cache-busting:', urlWithCacheBust);
      
      const tickets = parseTicketCSV(csvContent);
      
      if (!tickets || tickets.length === 0) {
        throw new Error('Aucun ticket trouvé dans le fichier CSV');
      }
      
      setAllTickets(tickets);
      
      // Calculer les statistiques initiales
      const stats = calculateStatistics(tickets);
      setStatistics(stats);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      console.error('Code d\'erreur:', error.code);
      console.error('Message d\'erreur:', error.message);
      console.error('Stack:', error.stack);
      
      // Message d'erreur plus clair selon le type d'erreur
      if (error.code === 'storage/unauthorized' || error.code === 'storage/permission-denied') {
        setError('Vous n\'avez pas les permissions nécessaires pour accéder aux fichiers. Veuillez vous reconnecter.');
      } else if (error.code === 'storage/object-not-found') {
        setError('Aucun fichier CSV trouvé. Veuillez uploader un fichier dans la page "Upload Revenus".');
      } else if (error.code === 'storage/unauthenticated') {
        setError('Vous devez être authentifié pour accéder aux fichiers. Veuillez vous reconnecter.');
      } else if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('network'))) {
        setError('Erreur de connexion réseau. Vérifiez votre connexion internet et réessayez. Si le problème persiste, vérifiez que Firebase Storage est activé dans votre projet Firebase.');
      } else {
        setError('Erreur lors du chargement des données: ' + (error.message || error.toString()) + (error.code ? ` (Code: ${error.code})` : ''));
      }
    } finally {
      setDataLoading(false);
    }
  };

  // Charger les dépenses depuis Firestore
  const loadExpenses = async () => {
    try {
      const expensesRef = collection(db, 'expenses');
      const q = query(expensesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = [];
      querySnapshot.forEach((docSnap) => {
        data.push({ id: docSnap.id, ...docSnap.data() });
      });
      setExpenses(data);
    } catch (error) {
      console.error('Erreur chargement dépenses:', error);
    }
  };

  // Charger les charges fixes depuis Firestore
  const loadFixedCharges = async () => {
    try {
      const chargesRef = collection(db, 'fixedCharges');
      const q = query(chargesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const chargesData = [];
      querySnapshot.forEach((doc) => {
        chargesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('Charges fixes chargées:', chargesData.length, chargesData);
      setFixedCharges(chargesData);
    } catch (error) {
      console.error('Erreur lors du chargement des charges fixes:', error);
    }
  };

  // Filtrer les tickets selon les dates sélectionnées
  const filteredTickets = useMemo(() => {
    if (!allTickets || allTickets.length === 0) return [];
    
    let filtered = [...allTickets];
    
    // Convertir les dates de filtrage
    let start = null;
    let end = null;
    
    if (startDate) {
      start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
    }
    
    if (endDate) {
      end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
    }
    
    // Appliquer les filtres
    filtered = filterTicketsByDate(filtered, start, end, excludedDates);
    
    // Log pour déboguer les filtres
    const datesInFiltered = [...new Set(filtered.map(t => t.date))].sort();
    console.log('🔍 Filtrage des tickets:', {
      totalTickets: allTickets.length,
      filteredTickets: filtered.length,
      startDate: startDate || 'aucune',
      endDate: endDate || 'aucune',
      excludedDates: excludedDates,
      datesInFiltered: datesInFiltered,
      firstDate: datesInFiltered[0],
      lastDate: datesInFiltered[datesInFiltered.length - 1],
      has13Feb: datesInFiltered.some(d => d.includes('13/02'))
    });
    
    return filtered;
  }, [allTickets, startDate, endDate, excludedDates]);

  // Calculer le total des charges fixes pour toute la période couverte par filteredTickets
  const totalFixedCharges = useMemo(() => {
    if (!fixedCharges || fixedCharges.length === 0) return 0;
    if (!filteredTickets || filteredTickets.length === 0) return 0;

    // Extraire tous les mois uniques couverts par les tickets filtrés
    const monthsSet = new Set();
    filteredTickets.forEach(ticket => {
      if (ticket.date) {
        const parts = ticket.date.split('/');
        if (parts.length === 3) {
          const year = parseInt(parts[2], 10);
          const month = parseInt(parts[1], 10);
          const monthKey = `${year}-${String(month).padStart(2, '0')}`;
          monthsSet.add(monthKey);
        }
      }
    });

    // Si aucun mois trouvé, retourner 0
    if (monthsSet.size === 0) return 0;

    // Pour chaque mois, calculer les charges fixes qui s'appliquent
    let totalCharges = 0;
    monthsSet.forEach(monthKey => {
      fixedCharges.forEach(charge => {
        const startMonth = charge.startMonth || charge.month; // Support ancien format
        const amount = parseFloat(charge.amount) || 0;
        // Si la charge a commencé avant ou pendant ce mois, elle s'applique
        if (startMonth && monthKey >= startMonth && amount > 0) {
          totalCharges += amount;
        }
      });
    });

    console.log('Calcul charges fixes totales pour la période:', {
      totalCharges: totalCharges,
      monthsCount: monthsSet.size,
      months: Array.from(monthsSet).sort(),
      fixedChargesCount: fixedCharges.length,
      fixedCharges: fixedCharges.map(c => ({ name: c.name, startMonth: c.startMonth || c.month, amount: c.amount }))
    });
    
    return totalCharges;
  }, [fixedCharges, filteredTickets]);

  // Dépenses par date et total (filtrées comme les tickets)
  const { expensesByDate, totalExpenses, expensesWithoutDate } = useMemo(() => {
    const byDate = {};
    let total = 0;
    const withoutDate = { total: 0, items: [] };

    if (!expenses || expenses.length === 0) {
      return { expensesByDate: byDate, totalExpenses: 0, expensesWithoutDate: withoutDate };
    }

    let start = null;
    let end = null;
    if (startDate) {
      start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
    }
    if (endDate) {
      end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
    }

    const excludedFormatted = (excludedDates || []).map(d =>
      d.includes('/') ? d : (() => {
        const [y, m, day] = d.split('-');
        return `${day}/${m}/${y}`;
      })()
    );

    expenses.forEach((exp) => {
      const amount = parseFloat(exp.amount) || 0;
      const item = { id: exp.id, description: exp.description || 'Sans description', amount };
      if (!exp.date) {
        withoutDate.total += amount;
        withoutDate.items.push(item);
        total += amount;
        return;
      }
      const [y, m, d] = exp.date.split('-');
      if (!y || !m || !d) return;
      const dateDDMMYYYY = `${d}/${m}/${y}`;
      if (excludedFormatted.includes(dateDDMMYYYY)) return;
      const expDate = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      if (start && expDate < start) return;
      if (end && expDate > end) return;
      if (!byDate[dateDDMMYYYY]) byDate[dateDDMMYYYY] = { total: 0, items: [] };
      byDate[dateDDMMYYYY].total += amount;
      byDate[dateDDMMYYYY].items.push(item);
      total += amount;
    });

    return { expensesByDate: byDate, totalExpenses: total, expensesWithoutDate: withoutDate };
  }, [expenses, startDate, endDate, excludedDates]);

  // Calculer les charges fixes mensuelles (pour un seul mois, pour l'affichage)
  const monthlyFixedCharges = useMemo(() => {
    if (!fixedCharges || fixedCharges.length === 0) return 0;

    // Déterminer le mois de référence (le mois le plus récent dans la période)
    let referenceMonth = null;
    
    if (filteredTickets && filteredTickets.length > 0) {
      // Utiliser le mois le plus récent des tickets filtrés
      let latestDate = null;
      filteredTickets.forEach(ticket => {
        if (ticket.date) {
          const parts = ticket.date.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const date = new Date(year, month, day);
            if (!latestDate || date > latestDate) {
              latestDate = date;
            }
          }
        }
      });
      if (latestDate) {
        const year = latestDate.getFullYear();
        const month = String(latestDate.getMonth() + 1).padStart(2, '0');
        referenceMonth = `${year}-${month}`;
      }
    } else if (endDate) {
      // Utiliser la date de fin du filtre
      const end = new Date(endDate);
      const year = end.getFullYear();
      const month = String(end.getMonth() + 1).padStart(2, '0');
      referenceMonth = `${year}-${month}`;
    } else if (startDate) {
      // Utiliser la date de début du filtre
      const start = new Date(startDate);
      const year = start.getFullYear();
      const month = String(start.getMonth() + 1).padStart(2, '0');
      referenceMonth = `${year}-${month}`;
    } else if (allTickets && allTickets.length > 0) {
      // Utiliser le mois le plus récent de tous les tickets
      let latestDate = null;
      allTickets.forEach(ticket => {
        if (ticket.date) {
          const parts = ticket.date.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const date = new Date(year, month, day);
            if (!latestDate || date > latestDate) {
              latestDate = date;
            }
          }
        }
      });
      if (latestDate) {
        const year = latestDate.getFullYear();
        const month = String(latestDate.getMonth() + 1).padStart(2, '0');
        referenceMonth = `${year}-${month}`;
      }
    } else {
      // Par défaut, utiliser le mois actuel
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      referenceMonth = `${year}-${month}`;
    }

    // Calculer le total des charges fixes pour ce mois unique
    // Les charges fixes sont récurrentes chaque mois à partir du mois de début
    let monthlyTotal = 0;
    if (referenceMonth) {
      fixedCharges.forEach(charge => {
        const startMonth = charge.startMonth || charge.month; // Support ancien format
        if (startMonth && referenceMonth >= startMonth) {
          monthlyTotal += charge.amount || 0;
        }
      });
    }

    console.log('Calcul charges fixes mensuelles:', {
      monthlyCharges: monthlyTotal,
      referenceMonth: referenceMonth,
      fixedChargesCount: fixedCharges.length,
      fixedCharges: fixedCharges.map(c => ({ name: c.name, startMonth: c.startMonth || c.month, amount: c.amount }))
    });
    
    return monthlyTotal;
  }, [fixedCharges, filteredTickets, allTickets, startDate, endDate]);

  // Recalculer les statistiques quand les filtres changent
  useEffect(() => {
    if (filteredTickets.length > 0) {
      const stats = calculateStatistics(filteredTickets);
      setStatistics(stats);
    } else if (allTickets.length > 0 && (startDate || endDate || excludedDates.length > 0)) {
      // Si des filtres sont appliqués mais aucun résultat
      setStatistics({
        totalRevenue: 0,
        totalTickets: 0,
        totalItems: 0,
        mostSoldPlate: null,
        leastSoldPlate: null,
        revenueByDay: {},
        revenueByMonth: {},
        paymentMethods: {},
        topPlates: [],
        topTables: [],
        averageTicketValue: 0
      });
    }
  }, [filteredTickets, allTickets, startDate, endDate, excludedDates]);

  // Ajouter une date exclue
  const addExcludedDate = () => {
    if (newExcludedDate) {
      // Convertir la date au format DD/MM/YYYY pour correspondre au format CSV
      const date = new Date(newExcludedDate);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;
      
      if (!excludedDates.includes(formattedDate)) {
        setExcludedDates([...excludedDates, formattedDate]);
        setNewExcludedDate('');
      }
    }
  };

  // Supprimer une date exclue
  const removeExcludedDate = (date) => {
    setExcludedDates(excludedDates.filter(d => d !== date));
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setStartDate('');
    setEndDate('');
    setExcludedDates([]);
    setNewExcludedDate('');
  };

  // Fonction pour rafraîchir les données
  const handleRefresh = async () => {
    console.log('🔄 Rafraîchissement des données...');
    setDataLoading(true);
    try {
      // Incrémenter la version pour forcer le rechargement des graphiques
      setDataVersion(prev => prev + 1);
      // Recharger toutes les données
      await loadCSVData();
      await loadFixedCharges();
      await loadExpenses();
      console.log('✅ Données rafraîchies avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du rafraîchissement:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // Formater un nombre avec séparateurs (espaces pour milliers, virgule pour décimales)
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    
    // Vérifier si le nombre a des décimales
    const hasDecimals = num % 1 !== 0;
    
    let formatted;
    if (hasDecimals) {
      // Si le nombre a des décimales, utiliser la virgule pour les décimales
      formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
      }).format(num);
    } else {
      // Si c'est un entier, pas de virgule, juste les espaces pour les milliers
      formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true
      }).format(num);
    }
    
    // Remplacer les espaces normaux par des espaces insécables plus visibles
    return formatted.replace(/\s/g, '\u00A0');
  };

  // Formater un revenu avec virgule seulement si le nombre a déjà des décimales (ex: 128 540,914 TND ou 128 540 TND)
  const formatRevenue = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    
    // Vérifier si le nombre a des décimales
    const hasDecimals = num % 1 !== 0;
    
    let formatted;
    if (hasDecimals) {
      // Si le nombre a des décimales, afficher jusqu'à 3 décimales
      formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
        useGrouping: true
      }).format(num);
    } else {
      // Si c'est un entier, pas de virgule, juste les espaces pour les milliers
      formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true
      }).format(num);
    }
    
    // Remplacer les espaces normaux par des espaces insécables plus visibles
    // Utiliser un espace insécable (non-breaking space) pour une meilleure visibilité
    return formatted.replace(/\s/g, '\u00A0');
  };

  // Obtenir le jour de la semaine depuis une date au format DD/MM/YYYY
  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length !== 3) return '';
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // mois 0-indexed
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[date.getDay()];
  };

  // Obtenir les tickets d'une date spécifique
  const getTicketsByDate = (dateStr) => {
    if (!dateStr || !filteredTickets) return [];
    return filteredTickets.filter(ticket => ticket.date === dateStr && ticket.total > 0);
  };

  // Ouvrir la popup avec les détails d'une date
  const handleViewDetails = (dateStr) => {
    setSelectedDateDetails(dateStr);
    setShowDetailsModal(true);
  };

  // Fermer la popup
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedDateDetails(null);
  };

  // Préparer les données pour les graphiques avec détails pour tooltips
  const prepareChartData = useMemo(() => {
    if (!statistics || !filteredTickets) return null;
    const expByDate = expensesByDate || {};
    
    // Vérifier que fixedCharges est disponible
    if (!fixedCharges) {
      console.warn('fixedCharges n\'est pas disponible dans prepareChartData');
    }

    // Créer un mapping des revenus par jour avec détails (addition exacte comme dans le CSV)
    const revenueByDayWithDetails = {};
    filteredTickets.forEach(ticket => {
      if (ticket.date && ticket.total > 0) {
        if (!revenueByDayWithDetails[ticket.date]) {
          revenueByDayWithDetails[ticket.date] = {
            revenue: 0,
            tickets: new Set(),
            items: 0
          };
        }
        const prev = revenueByDayWithDetails[ticket.date].revenue;
        const m = ticket.totalMillimes ?? toMillimes(ticket.total);
        revenueByDayWithDetails[ticket.date].revenue = fromMillimes(toMillimes(prev) + m);
        revenueByDayWithDetails[ticket.date].tickets.add(ticket.numTicket);
        revenueByDayWithDetails[ticket.date].items += ticket.quantite;
      }
    });
    
    // Log pour vérifier toutes les dates disponibles
    const allDates = Object.keys(revenueByDayWithDetails).sort((a, b) => {
      const parseDate = (dateStr) => {
        const parts = dateStr.split('/');
        if (parts.length !== 3) return new Date(0);
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      };
      return parseDate(a).getTime() - parseDate(b).getTime();
    });
    console.log('📅 Toutes les dates disponibles dans filteredTickets:', {
      total: allDates.length,
      first: allDates[0],
      last: allDates[allDates.length - 1],
      allDates: allDates
    });

    // Graphique des revenus par jour - Toutes les données triées
    const revenueByDayEntries = Object.entries(revenueByDayWithDetails)
      .map(([date, data]) => [date, data.revenue, data.tickets.size, data.items])
      .sort((a, b) => {
        // Convertir DD/MM/YYYY en Date pour un tri correct
        const parseDate = (dateStr) => {
          const parts = dateStr.split('/');
          if (parts.length !== 3) return new Date(0);
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // mois 0-indexed
          const year = parseInt(parts[2], 10);
          return new Date(year, month, day);
        };
        const dateA = parseDate(a[0]);
        const dateB = parseDate(b[0]);
        return dateA.getTime() - dateB.getTime();
      });
    
    // Si on veut limiter, on prend les derniers jours, sinon toutes les données
    // Utiliser <= au lieu de < pour inclure toutes les dates si daysToShow correspond exactement
    const displayedEntries = daysToShow > 0 && daysToShow <= revenueByDayEntries.length
      ? revenueByDayEntries.slice(-daysToShow)
      : revenueByDayEntries;
    
    // Log pour déboguer
    if (revenueByDayEntries.length > 0) {
      console.log('📊 Dates dans le graphique:', {
        total: revenueByDayEntries.length,
        first: revenueByDayEntries[0]?.[0],
        last: revenueByDayEntries[revenueByDayEntries.length - 1]?.[0],
        displayed: displayedEntries.length,
        displayedFirst: displayedEntries[0]?.[0],
        displayedLast: displayedEntries[displayedEntries.length - 1]?.[0],
        daysToShow: daysToShow,
        condition: daysToShow > 0 && daysToShow <= revenueByDayEntries.length,
        allDates: revenueByDayEntries.map(e => e[0]),
        displayedDates: displayedEntries.map(e => e[0])
      });
      
      // Vérifier si la date 13/02 est présente
      const has13Feb = revenueByDayEntries.some(e => e[0].includes('13/02'));
      const displayedHas13Feb = displayedEntries.some(e => e[0].includes('13/02'));
      console.log('🔍 Vérification date 13/02:', {
        inAllDates: has13Feb,
        inDisplayed: displayedHas13Feb,
        allDatesWith13Feb: revenueByDayEntries.filter(e => e[0].includes('13/02')).map(e => e[0]),
        displayedWith13Feb: displayedEntries.filter(e => e[0].includes('13/02')).map(e => e[0])
      });
    }

    // Power BI Color Palette
    const powerBIColors = [
      '#3b82f6', // Blue
      '#10b981', // Green
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#8b5cf6', // Purple
      '#ec4899', // Pink
      '#06b6d4', // Cyan
      '#84cc16', // Lime
      '#f97316', // Orange
      '#6366f1', // Indigo
    ];

    // Stocker les détails pour les tooltips (avec dépenses et net)
    const revenueChartDetails = displayedEntries.map(([date, revenue, tickets, items]) => {
      const dayExpenses = expByDate[date] || { total: 0, items: [] };
      const net = revenue - dayExpenses.total;
      return { date, revenue, expenses: dayExpenses.total, net, tickets, items };
    });

    const revenueChartData = {
      labels: displayedEntries.map(([date]) => date),
      datasets: [
        {
          label: 'Revenu Net (TND)',
          data: displayedEntries.map(([date, revenue]) => {
            const dayExpenses = (expByDate[date] || {}).total || 0;
            return Math.max(0, revenue - dayExpenses);
          }),
          backgroundColor: '#10b981',
          borderColor: '#059669',
          borderWidth: 2,
          borderRadius: 4,
        },
        {
          label: 'Dépenses (TND)',
          data: displayedEntries.map(([date]) => (expByDate[date] || {}).total || 0),
          backgroundColor: '#f59e0b',
          borderColor: '#d97706',
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
      details: revenueChartDetails,
    };

    // Graphique des revenus par mois avec charges fixes et profit
    // Les clés dans statistics.revenueByMonth sont au format "YYYY-MM"
    const revenueByMonthEntries = Object.entries(statistics.revenueByMonth)
      .sort((a, b) => a[0].localeCompare(b[0]));

    // Calculer les charges fixes par mois
    // Les charges fixes sont récurrentes chaque mois à partir du mois de début
    const fixedChargesByMonth = {};
    
    console.log('=== DEBUG CHARGES FIXES ===');
    console.log('fixedCharges:', fixedCharges);
    console.log('fixedCharges length:', fixedCharges?.length);
    console.log('revenueByMonthEntries:', revenueByMonthEntries);
    
    // Pour chaque mois dans les revenus, calculer le total des charges fixes
    // Les clés sont déjà au format "YYYY-MM" (ex: "2025-12")
    revenueByMonthEntries.forEach(([monthKey]) => {
      // monthKey est déjà au format "YYYY-MM"
      // Calculer le total des charges fixes pour ce mois
      let totalForMonth = 0;
      if (fixedCharges && Array.isArray(fixedCharges) && fixedCharges.length > 0) {
        fixedCharges.forEach(charge => {
          const startMonth = charge.startMonth || charge.month; // Support ancien format
          const amount = parseFloat(charge.amount) || 0;
          
          console.log(`Vérification charge: ${charge.name}, startMonth: ${startMonth}, monthKey: ${monthKey}, amount: ${amount}`);
          
          // Normaliser le format du startMonth si nécessaire
          let normalizedStartMonth = startMonth;
          if (startMonth && !startMonth.includes('-')) {
            // Si le format n'est pas YYYY-MM, essayer de le convertir
            console.warn(`Format de mois suspect: ${startMonth}`);
          }
          
          // Vérifier que le mois est >= au mois de début (charges fixes récurrentes mensuellement)
          const isApplicable = startMonth && monthKey >= normalizedStartMonth && amount > 0;
          
          if (isApplicable) {
            totalForMonth += amount;
            console.log(`✓ Charge fixe appliquée (mensuelle): ${charge.name} (depuis ${startMonth}) -> ${monthKey}: ${amount} TND`);
          } else {
            const reason = !startMonth ? 'pas de startMonth' : 
                          (monthKey < normalizedStartMonth ? `mois ${monthKey} < startMonth ${normalizedStartMonth}` : 
                          (amount <= 0 ? 'montant <= 0' : 'raison inconnue'));
            console.log(`✗ Charge fixe NON appliquée: ${charge.name} (${startMonth}) -> ${monthKey}: ${reason}`);
          }
        });
      } else {
        console.log('Aucune charge fixe disponible ou fixedCharges n\'est pas un tableau');
      }
      
      fixedChargesByMonth[monthKey] = totalForMonth;
      if (totalForMonth > 0) {
        console.log(`✓ Charges fixes totales pour ${monthKey}:`, totalForMonth, 'TND');
      } else {
        console.log(`✗ Aucune charge fixe pour ${monthKey}`);
      }
    });
    
    console.log('fixedChargesByMonth final:', fixedChargesByMonth);
    console.log('=== FIN DEBUG ===');

    // Formater les mois pour l'affichage (ex: "2025-12" -> "Décembre 2025")
    const formatMonthForChart = (monthKey) => {
      // monthKey est au format "YYYY-MM"
      if (monthKey.includes('-')) {
        const [year, month] = monthKey.split('-');
        const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
      }
      return monthKey;
    };

    // Dépenses par mois (depuis expensesByDate)
    const expensesByMonth = {};
    Object.entries(expByDate || {}).forEach(([dateStr, data]) => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const monthKey = `${parts[2]}-${parts[1]}`;
        if (!expensesByMonth[monthKey]) expensesByMonth[monthKey] = 0;
        expensesByMonth[monthKey] += data.total || 0;
      }
    });
    // Préparer les données pour le graphique avec logs de débogage
    const revenueData = revenueByMonthEntries.map(([monthKey]) => {
      const revenue = statistics.revenueByMonth[monthKey] || 0;
      return revenue;
    });
    
    const chargesData = revenueByMonthEntries.map(([monthKey]) => {
      const charges = fixedChargesByMonth[monthKey] || 0;
      return charges;
    });
    
    const expensesData = revenueByMonthEntries.map(([monthKey]) => {
      return expensesByMonth[monthKey] || 0;
    });
    
    const profitData = revenueByMonthEntries.map(([monthKey], index) => {
      const revenue = statistics.revenueByMonth[monthKey] || 0;
      const charges = fixedChargesByMonth[monthKey] || 0;
      const monthExpenses = expensesByMonth[monthKey] || 0;
      const profit = revenue - charges - monthExpenses;
      
      // Log détaillé pour chaque mois
      console.log(`Mois ${index + 1} (${formatMonthForChart(monthKey)}): Revenus=${revenue}, Charges=${charges}, Dépenses=${monthExpenses}, Profit=${profit}`);
      
      return profit;
    });
    
    console.log('=== DONNÉES GRAPHIQUE MENSUEL ===');
    console.log('Labels:', revenueByMonthEntries.map(([monthKey]) => formatMonthForChart(monthKey)));
    console.log('Revenus:', revenueData);
    console.log('Charges fixes:', chargesData);
    console.log('Profit net:', profitData);
    
    // Vérifier que le profit net est différent des revenus
    const profitDifferent = profitData.some((profit, index) => profit !== revenueData[index]);
    console.log('Profit net différent des revenus?', profitDifferent);
    if (!profitDifferent && chargesData.some(c => c > 0)) {
      console.error('ERREUR: Le profit net est identique aux revenus alors que des charges fixes existent!');
    }
    
    console.log('=== FIN DONNÉES ===');

    const monthlyChartData = {
      labels: revenueByMonthEntries.map(([monthKey]) => formatMonthForChart(monthKey)),
      datasets: [
        {
          label: 'Revenus (TND)',
          data: revenueData,
          backgroundColor: '#3b82f6',
          borderColor: '#2563eb',
          borderWidth: 2,
          borderRadius: 4,
        },
        {
          label: 'Dépenses (TND)',
          data: expensesData,
          backgroundColor: '#f59e0b',
          borderColor: '#d97706',
          borderWidth: 2,
          borderRadius: 4,
        },
        {
          label: 'Charges Fixes (TND)',
          data: chargesData,
          backgroundColor: '#ef4444',
          borderColor: '#dc2626',
          borderWidth: 2,
          borderRadius: 4,
        },
        {
          label: 'Profit Net (TND)',
          data: profitData,
          backgroundColor: '#10b981',
          borderColor: '#059669',
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    };

    // Graphique Top 10 plats avec couleurs variées
    const topPlatesData = {
      labels: statistics.topPlates.slice(0, 10).map(p => p.article.length > 25 ? p.article.substring(0, 25) + '...' : p.article),
      datasets: [
        {
          label: 'Quantité vendue',
          data: statistics.topPlates.slice(0, 10).map(p => p.count),
          backgroundColor: powerBIColors.slice(0, 10),
          borderColor: '#ffffff',
          borderWidth: 1,
        },
      ],
    };

    // Graphique méthodes de paiement avec palette Power BI
    const paymentMethodsEntries = Object.entries(statistics.paymentMethods)
      .sort((a, b) => b[1] - a[1]);

    const paymentChartData = {
      labels: paymentMethodsEntries.map(([method]) => method),
      datasets: [
        {
          data: paymentMethodsEntries.map(([, amount]) => amount),
          backgroundColor: powerBIColors.slice(0, paymentMethodsEntries.length),
          borderColor: '#ffffff',
          borderWidth: 2,
        },
      ],
    };

    return {
      revenueChartData,
      monthlyChartData,
      topPlatesData,
      paymentChartData,
    };
  }, [statistics, filteredTickets, fixedCharges, daysToShow, expensesByDate]);

  const chartData = prepareChartData;
  
  // Stocker les détails pour les tooltips (accessible dans les callbacks)
  const revenueChartDetailsRef = React.useRef(null);
  const chartRef = React.useRef(null);
  const chartContainerRef = React.useRef(null);
  
  if (chartData && chartData.revenueChartData) {
    revenueChartDetailsRef.current = chartData.revenueChartData.details;
  }

  // Ajouter un gestionnaire de clic sur le canvas du graphique
  React.useEffect(() => {
    if (chartContainerRef.current && chartRef.current) {
      const canvas = chartContainerRef.current.querySelector('canvas');
      if (canvas) {
        const handleCanvasClick = (event) => {
          const chart = chartRef.current;
          if (chart && chart.canvas) {
            const points = chart.getElementsAtEventForMode(event, 'index', { intersect: false }, true);
            if (points && points.length > 0) {
              const firstPoint = points[0];
              const index = firstPoint.index;
              const details = revenueChartDetailsRef.current;
              if (details && details[index]) {
                const date = details[index].date;
                console.log('Canvas click detected, opening details for date:', date);
                handleViewDetails(date);
              }
            }
          }
        };
        
        canvas.style.cursor = 'pointer';
        canvas.addEventListener('click', handleCanvasClick);
        return () => {
          canvas.removeEventListener('click', handleCanvasClick);
        };
      }
    }
  }, [chartData, filteredTickets]);

  // Calculer tous les plats triés
  const sortedPlates = useMemo(() => {
    if (!statistics || !statistics.plateCounts) return [];
    
    // Créer une liste de tous les plats avec leurs statistiques
    const allPlates = Object.entries(statistics.plateCounts)
      .map(([article, count]) => ({
        article,
        count,
        revenue: statistics.plateRevenue?.[article] || 0
      }))
      .filter(plate => plate.count > 0 && plate.revenue > 0); // Filtrer les plats avec quantité > 0 et revenus > 0

    // Trier selon les critères sélectionnés
    const sorted = [...allPlates].sort((a, b) => {
      let comparison = 0;
      
      if (platesSortBy === 'count') {
        comparison = a.count - b.count;
      } else if (platesSortBy === 'revenue') {
        comparison = a.revenue - b.revenue;
      } else if (platesSortBy === 'name') {
        comparison = a.article.localeCompare(b.article, 'fr');
      } else if (platesSortBy === 'rank') {
        // Pour le rang, on trie par quantité décroissante (rang naturel)
        comparison = b.count - a.count;
      }
      
      return platesSortOrder === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }, [statistics, platesSortBy, platesSortOrder]);

  if (loading) {
    return (
      <div className="revenue-dashboard-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="revenue-dashboard-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          
          <h1>Dashboard des Revenus</h1>
          
          <div className="header-actions">
            <button
              onClick={handleRefresh}
              className="refresh-btn"
              title="Rafraîchir les données"
              disabled={dataLoading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {dataLoading ? 'Chargement...' : 'Rafraîchir'}
            </button>
            
            {csvDownloadUrl && (
              <a
                href={csvDownloadUrl}
                download="merged_revenue.csv"
                className="download-csv-btn"
                title="Télécharger le CSV complet"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Télécharger CSV
              </a>
            )}
          </div>
        </div>

        <div className="dashboard-body">
          {error && <div className="error-message">{error}</div>}
          
          {dataLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Chargement des données...</p>
            </div>
          ) : statistics ? (
            <>
              {/* Filtres de dates */}
              <div className="dashboard-section filters-section">
                <h2>Filtres de Dates</h2>
                <div className="filters-grid">
                  <div className="filter-group">
                    <label>Date de début</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="date-input"
                    />
                  </div>
                  <div className="filter-group">
                    <label>Date de fin</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="date-input"
                    />
                  </div>
                  <div className="filter-group">
                    <label>Exclure une date</label>
                    <div className="exclude-date-input">
                      <input
                        type="date"
                        value={newExcludedDate}
                        onChange={(e) => setNewExcludedDate(e.target.value)}
                        className="date-input"
                        placeholder="DD/MM/YYYY"
                      />
                      <button onClick={addExcludedDate} className="add-exclude-btn">
                        Ajouter
                      </button>
                    </div>
                  </div>
                  <div className="filter-group">
                    <button onClick={resetFilters} className="reset-filters-btn">
                      Réinitialiser
                    </button>
                  </div>
                </div>
                
                {excludedDates.length > 0 && (
                  <div className="excluded-dates-list">
                    <label>Dates exclues:</label>
                    <div className="excluded-dates-tags">
                      {excludedDates.map((date, index) => (
                        <span key={index} className="excluded-date-tag">
                          {date}
                          <button onClick={() => removeExcludedDate(date)} className="remove-date-btn">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Statistiques principales */}
              <div className="stats-grid-main">
                <div className="stat-card-large revenue-card">
                  <div className="stat-icon">💰</div>
                  <div className="stat-content">
                    <h3>Revenus Totaux</h3>
                    <p className="stat-value-large">{formatRevenue(statistics.totalRevenue)} TND</p>
                  </div>
                </div>
                
                <div className="stat-card-large">
                  <div className="stat-icon">🍽️</div>
                  <div className="stat-content">
                    <h3>Articles Vendus</h3>
                    <p className="stat-value-large">{formatNumber(statistics.totalItems)}</p>
                  </div>
                </div>

                <div className="stat-card-large" style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' }}>
                  <div className="stat-icon">💸</div>
                  <div className="stat-content">
                    <h3>Charges Fixes</h3>
                    <p className="stat-value-large" style={{ color: monthlyFixedCharges > 0 ? '#dc2626' : '#6b7280' }}>
                      {formatRevenue(monthlyFixedCharges)} TND
                    </p>
                    {fixedCharges.length > 0 && (
                      <p style={{ fontSize: '12px', color: '#991b1b', marginTop: '4px' }}>
                        {fixedCharges.length} charge{fixedCharges.length > 1 ? 's' : ''} fixe{fixedCharges.length > 1 ? 's' : ''} enregistrée{fixedCharges.length > 1 ? 's' : ''} (mensuel)
                        {monthlyFixedCharges === 0 && ' (hors période)'}
                      </p>
                    )}
                    {fixedCharges.length === 0 && (
                      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        Aucune charge fixe
                      </p>
                    )}
                  </div>
                </div>

                <div className="stat-card-large" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                  <div className="stat-icon">📤</div>
                  <div className="stat-content">
                    <h3>Dépenses</h3>
                    <p className="stat-value-large" style={{ color: totalExpenses > 0 ? '#b45309' : '#6b7280' }}>
                      {formatRevenue(totalExpenses)} TND
                    </p>
                    {expensesWithoutDate.items?.length > 0 && (
                      <p style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                        dont {formatRevenue(expensesWithoutDate.total)} TND sans date
                      </p>
                    )}
                  </div>
                </div>

                <div className="stat-card-large" style={{ background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' }}>
                  <div className="stat-icon">📈</div>
                  <div className="stat-content">
                    <h3>Profit Net</h3>
                    <p className="stat-value-large" style={{ color: '#059669' }}>
                      {formatRevenue((statistics?.totalRevenue || 0) - totalFixedCharges - totalExpenses)} TND
                    </p>
                    {statistics && statistics.totalRevenue > 0 && (
                      <p style={{ fontSize: '12px', color: '#047857', marginTop: '4px' }}>
                        {((((statistics.totalRevenue - totalFixedCharges - totalExpenses) / statistics.totalRevenue) * 100) || 0).toFixed(1)}% de marge
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Graphiques */}
              {chartData && (
                <>
                  <div className="dashboard-section">
                    <div className="chart-header-controls">
                      <h2>Revenus par Jour</h2>
                      <div className="days-selector">
                        <label>Afficher:</label>
                        <select 
                          value={daysToShow} 
                          onChange={(e) => setDaysToShow(parseInt(e.target.value) || 0)}
                          className="days-select-input"
                        >
                          <option value="0">Toutes les données</option>
                          <option value="7">7 derniers jours</option>
                          <option value="15">15 derniers jours</option>
                          <option value="30">30 derniers jours</option>
                          <option value="60">60 derniers jours</option>
                          <option value="90">90 derniers jours</option>
                        </select>
                      </div>
                    </div>
                    <div className="chart-container scrollable-chart">
                      <div className="chart-wrapper" ref={chartContainerRef}>
                        <Bar 
                          key={`revenue-chart-${dataVersion}`}
                          ref={chartRef}
                          data={chartData.revenueChartData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            interaction: {
                              intersect: false,
                              mode: 'index'
                            },
                            layout: {
                              padding: {
                                right: isMobile ? 5 : 20,
                                bottom: isMobile ? 25 : 40,
                                left: isMobile ? 0 : 10,
                                top: isMobile ? 0 : 10
                              }
                            },
                            plugins: {
                              legend: {
                                display: false,
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                padding: 16,
                                titleFont: {
                                  size: 14,
                                  weight: '600',
                                  family: 'Inter'
                                },
                                bodyFont: {
                                  size: 13,
                                  family: 'Inter'
                                },
                                borderColor: '#3b82f6',
                                borderWidth: 2,
                                displayColors: false,
                                footerFont: {
                                  size: 12,
                                  family: 'Inter',
                                  style: 'italic'
                                },
                                footerColor: '#3b82f6',
                                callbacks: {
                                  title: function(context) {
                                    const dateLabel = context[0].label;
                                    const dayOfWeek = getDayOfWeek(dateLabel);
                                    return dayOfWeek ? `${dayOfWeek} ${dateLabel}` : `Date: ${dateLabel}`;
                                  },
                                  label: function(context) {
                                    const index = context.dataIndex;
                                    const details = revenueChartDetailsRef.current;
                                    if (details && details[index]) {
                                      const d = details[index];
                                      const lines = [
                                        'Revenus bruts: ' + formatRevenue(d.revenue) + ' TND',
                                        'Dépenses: ' + formatRevenue(d.expenses) + ' TND',
                                        'Revenu net: ' + formatRevenue(d.net) + ' TND',
                                        'Tickets: ' + formatNumber(d.tickets),
                                        'Articles: ' + formatNumber(d.items)
                                      ];
                                      return lines;
                                    }
                                    return 'Revenu net: ' + formatRevenue(context.parsed.y) + ' TND';
                                  },
                                  footer: function(context) {
                                    return 'Cliquez sur la barre pour voir les détails';
                                  }
                                }
                              },
                              onClick: function(event, elements, chart) {
                                if (elements && elements.length > 0) {
                                  const index = elements[0].index;
                                  const details = revenueChartDetailsRef.current;
                                  if (details && details[index]) {
                                    const date = details[index].date;
                                    console.log('Click detected, opening details for date:', date);
                                    handleViewDetails(date);
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                grid: {
                                  display: false
                                },
                                ticks: {
                                  font: {
                                    size: isMobile ? 8 : 11,
                                    family: 'Inter'
                                  },
                                  color: '#6b7280',
                                  maxRotation: isMobile ? 90 : 45,
                                  minRotation: isMobile ? 90 : 45,
                                  autoSkip: true,
                                  maxTicksLimit: isMobile ? 8 : undefined,
                                  padding: isMobile ? 2 : 8
                                }
                              },
                              y: {
                                beginAtZero: true,
                                grid: {
                                  color: 'rgba(0, 0, 0, 0.05)',
                                  display: !isMobile
                                },
                                ticks: {
                                  font: {
                                    size: isMobile ? 8 : 11,
                                    family: 'Inter'
                                  },
                                  color: '#6b7280',
                                  callback: function(value) {
                                    if (isMobile && value >= 1000) {
                                      return (value / 1000).toFixed(1) + 'k TND';
                                    }
                                    return formatRevenue(value) + ' TND';
                                  },
                                  maxTicksLimit: isMobile ? 4 : undefined,
                                  padding: isMobile ? 2 : 8
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="charts-grid">
                    <div className="dashboard-section">
                      <h2>Revenus par Mois (avec Charges Fixes et Profit)</h2>
                      <div className="chart-container">
                        <Bar 
                          key={`monthly-chart-${dataVersion}`}
                          data={chartData.monthlyChartData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'top',
                                labels: {
                                  usePointStyle: true,
                                  padding: 15,
                                  font: {
                                    size: 12,
                                    family: 'Inter',
                                    weight: '500'
                                  }
                                }
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                padding: 12,
                                titleFont: {
                                  size: 14,
                                  weight: '600',
                                  family: 'Inter'
                                },
                                bodyFont: {
                                  size: 13,
                                  family: 'Inter'
                                },
                                borderColor: '#3b82f6',
                                borderWidth: 1,
                                callbacks: {
                                  label: function(context) {
                                    const label = context.dataset.label || '';
                                    const value = formatRevenue(context.parsed.y);
                                    return `${label}: ${value} TND`;
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                grid: {
                                  display: false
                                },
                                ticks: {
                                  font: {
                                    size: 11,
                                    family: 'Inter'
                                  },
                                  color: '#6b7280'
                                }
                              },
                              y: {
                                beginAtZero: true,
                                grid: {
                                  color: 'rgba(0, 0, 0, 0.05)'
                                },
                                ticks: {
                                  font: {
                                    size: 11,
                                    family: 'Inter'
                                  },
                                  color: '#6b7280',
                                  callback: function(value) {
                                    return formatRevenue(value) + ' TND';
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="dashboard-section">
                      <h2>Top 10 Plats</h2>
                      <div className="chart-container">
                        <Bar 
                          key={`top-plates-chart-${dataVersion}`}
                          data={chartData.topPlatesData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            indexAxis: 'y',
                            plugins: {
                              legend: {
                                display: false,
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                padding: 12,
                                titleFont: {
                                  size: 14,
                                  weight: '600',
                                  family: 'Inter'
                                },
                                bodyFont: {
                                  size: 13,
                                  family: 'Inter'
                                },
                                borderColor: '#3b82f6',
                                borderWidth: 1,
                                callbacks: {
                                  label: function(context) {
                                    return 'Quantité: ' + formatNumber(context.parsed.x);
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                beginAtZero: true,
                                grid: {
                                  color: 'rgba(0, 0, 0, 0.05)'
                                },
                                ticks: {
                                  font: {
                                    size: 11,
                                    family: 'Inter'
                                  },
                                  color: '#6b7280'
                                }
                              },
                              y: {
                                grid: {
                                  display: false
                                },
                                ticks: {
                                  font: {
                                    size: 11,
                                    family: 'Inter'
                                  },
                                  color: '#6b7280'
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="dashboard-section">
                    <h2>Répartition des Méthodes de Paiement</h2>
                    <div className="chart-container doughnut-chart">
                      <Doughnut 
                        key={`payment-chart-${dataVersion}`}
                        data={chartData.paymentChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'right',
                              labels: {
                                usePointStyle: true,
                                padding: 15,
                                font: {
                                  size: 12,
                                  weight: '500',
                                  family: 'Inter'
                                },
                                color: '#6b7280'
                              }
                            },
                            tooltip: {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              padding: 12,
                              titleFont: {
                                size: 14,
                                weight: '600',
                                family: 'Inter'
                              },
                              bodyFont: {
                                size: 13,
                                family: 'Inter'
                              },
                              borderColor: '#3b82f6',
                              borderWidth: 1,
                              callbacks: {
                                label: function(context) {
                                  const label = context.label || '';
                                  const value = formatNumber(context.parsed);
                                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                  const percentage = ((context.parsed / total) * 100).toFixed(1);
                                  return `${label}: ${value} TND (${percentage}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Tous les Plats */}
              <div className="dashboard-section">
                <h2>Tous les Plats</h2>
                <div className="top-plates-table">
                  <table>
                    <thead>
                      <tr>
                        <th 
                          className={`sortable-header ${platesSortBy === 'rank' ? 'sorted' : ''}`}
                          onClick={() => handleColumnSort('rank')}
                        >
                          <div className="header-content">
                            <span>Rang</span>
                            <span className="sort-arrows">
                              <span className={`arrow-up ${platesSortBy === 'rank' && platesSortOrder === 'asc' ? 'active' : ''}`}>▲</span>
                              <span className={`arrow-down ${platesSortBy === 'rank' && platesSortOrder === 'desc' ? 'active' : ''}`}>▼</span>
                            </span>
                          </div>
                        </th>
                        <th 
                          className={`sortable-header ${platesSortBy === 'name' ? 'sorted' : ''}`}
                          onClick={() => handleColumnSort('name')}
                        >
                          <div className="header-content">
                            <span>Article</span>
                            <span className="sort-arrows">
                              <span className={`arrow-up ${platesSortBy === 'name' && platesSortOrder === 'asc' ? 'active' : ''}`}>▲</span>
                              <span className={`arrow-down ${platesSortBy === 'name' && platesSortOrder === 'desc' ? 'active' : ''}`}>▼</span>
                            </span>
                          </div>
                        </th>
                        <th 
                          className={`sortable-header ${platesSortBy === 'count' ? 'sorted' : ''}`}
                          onClick={() => handleColumnSort('count')}
                        >
                          <div className="header-content">
                            <span>Quantité</span>
                            <span className="sort-arrows">
                              <span className={`arrow-up ${platesSortBy === 'count' && platesSortOrder === 'asc' ? 'active' : ''}`}>▲</span>
                              <span className={`arrow-down ${platesSortBy === 'count' && platesSortOrder === 'desc' ? 'active' : ''}`}>▼</span>
                            </span>
                          </div>
                        </th>
                        <th 
                          className={`sortable-header ${platesSortBy === 'revenue' ? 'sorted' : ''}`}
                          onClick={() => handleColumnSort('revenue')}
                        >
                          <div className="header-content">
                            <span>Revenus</span>
                            <span className="sort-arrows">
                              <span className={`arrow-up ${platesSortBy === 'revenue' && platesSortOrder === 'asc' ? 'active' : ''}`}>▲</span>
                              <span className={`arrow-down ${platesSortBy === 'revenue' && platesSortOrder === 'desc' ? 'active' : ''}`}>▼</span>
                            </span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlates.map((plate, index) => (
                        <tr key={plate.article}>
                          <td className="rank-cell">{index + 1}</td>
                          <td className="article-cell">{plate.article}</td>
                          <td className="quantity-cell">{formatNumber(plate.count)}</td>
                          <td className="revenue-cell">{formatRevenue(plate.revenue)} TND</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top Tables */}
              <div className="dashboard-section">
                <h2>Tables les Plus Utilisées</h2>
                <div className="top-tables-grid">
                  {statistics.topTables.map((table, index) => (
                    <div key={index} className="table-card">
                      <span className="table-number">Table {table.table}</span>
                      <span className="table-count">{formatNumber(table.count)} utilisations</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>Aucune donnée disponible</p>
            </div>
          )}
        </div>
      </div>

      {/* Popup des détails du jour */}
      {showDetailsModal && selectedDateDetails && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Détails du {getDayOfWeek(selectedDateDetails)} {selectedDateDetails}
              </h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                const dayTickets = getTicketsByDate(selectedDateDetails);
                const dayExpenses = expensesByDate[selectedDateDetails] || { total: 0, items: [] };
                const dayRevenue = dayTickets.reduce((sum, t) => sum + (t.totalMillimes ?? toMillimes(t.total)), 0);
                const dayNet = fromMillimes(dayRevenue) - dayExpenses.total;
                if (dayTickets.length === 0 && dayExpenses.items.length === 0) {
                  return <p className="no-data-message">Aucun achat ni dépense pour cette journée</p>;
                }
                
                // Grouper par ticket
                const ticketsMap = {};
                dayTickets.forEach(ticket => {
                  if (!ticketsMap[ticket.numTicket]) {
                    ticketsMap[ticket.numTicket] = {
                      numTicket: ticket.numTicket,
                      date: ticket.date,
                      heure: ticket.heure,
                      numTable: ticket.numTable,
                      nbrCouverts: ticket.nbrCouverts,
                      methodePaiement: ticket.methodePaiement,
                      items: []
                    };
                  }
                  ticketsMap[ticket.numTicket].items.push({
                    article: ticket.article,
                    prixUnitaire: ticket.prixUnitaire,
                    prixUnitaireMillimes: ticket.prixUnitaireMillimes ?? toMillimes(ticket.prixUnitaire),
                    quantite: ticket.quantite,
                    total: ticket.total,
                    totalMillimes: ticket.totalMillimes ?? toMillimes(ticket.total)
                  });
                });

                const ticketsList = Object.values(ticketsMap);

                return (
                  <div className="tickets-details-container">
                    {(dayTickets.length > 0 || dayExpenses.items.length > 0) && (
                      <div className="day-summary-card" style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                        <strong>Résumé du {selectedDateDetails}</strong>
                        <div style={{ display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap' }}>
                          {dayTickets.length > 0 && (
                            <span>Revenus: {formatRevenue(fromMillimes(dayRevenue))} TND</span>
                          )}
                          {dayExpenses.items.length > 0 && (
                            <span style={{ color: '#b45309' }}>Dépenses: {formatRevenue(dayExpenses.total)} TND</span>
                          )}
                          <span style={{ color: '#059669', fontWeight: 600 }}>Revenu net: {formatRevenue(dayNet)} TND</span>
                        </div>
                      </div>
                    )}
                    {dayExpenses.items.length > 0 && (
                      <div className="expenses-detail-section" style={{ marginBottom: 24 }}>
                        <h4 style={{ margin: '0 0 8px 0' }}>Dépenses du jour</h4>
                        <table className="ticket-items-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr><th>Description</th><th>Montant</th></tr>
                          </thead>
                          <tbody>
                            {dayExpenses.items.map((item, i) => (
                              <tr key={i}>
                                <td>{item.description}</td>
                                <td><strong>{formatRevenue(item.amount)} TND</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p style={{ margin: '8px 0 0 0', fontWeight: 600 }}>Total dépenses: {formatRevenue(dayExpenses.total)} TND</p>
                      </div>
                    )}
                    {ticketsList.map((ticket, ticketIndex) => {
                      const ticketTotal = fromMillimes(ticket.items.reduce((sum, item) => sum + (item.totalMillimes ?? toMillimes(item.total)), 0));
                      return (
                        <div key={ticketIndex} className="ticket-detail-card">
                          <div className="ticket-header">
                            <div className="ticket-info">
                              <span className="ticket-number">Ticket #{ticket.numTicket}</span>
                              <span className="ticket-time">{ticket.heure}</span>
                              {ticket.numTable && ticket.numTable !== '0' && (
                                <span className="ticket-table">Table {ticket.numTable}</span>
                              )}
                              {ticket.nbrCouverts > 0 && (
                                <span className="ticket-covers">{ticket.nbrCouverts} couverts</span>
                              )}
                            </div>
                            <div className="ticket-total">
                              <strong>Total: {formatRevenue(ticketTotal)} TND</strong>
                              {ticket.methodePaiement && ticket.methodePaiement !== '-' && (
                                <span className="payment-method">({ticket.methodePaiement})</span>
                              )}
                            </div>
                          </div>
                          <table className="ticket-items-table">
                            <thead>
                              <tr>
                                <th>Article</th>
                                <th>Prix Unitaire</th>
                                <th>Quantité</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ticket.items.map((item, itemIndex) => (
                                <tr key={itemIndex}>
                                  <td>{item.article}</td>
                                  <td>{formatRevenue(item.prixUnitaireMillimes != null ? fromMillimes(item.prixUnitaireMillimes) : item.prixUnitaire)} TND</td>
                                  <td>{item.quantite}</td>
                                  <td><strong>{formatRevenue(item.totalMillimes != null ? fromMillimes(item.totalMillimes) : item.total)} TND</strong></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueDashboardPage;
